import React from 'react'
import { useTranslation } from 'react-i18next'
import { HiPlay, HiClock, HiEye } from 'react-icons/hi'

/**
 * VideoCard Component
 * Displays a video thumbnail card with metadata
 */
const VideoCard = ({ video, onClick }) => {
  const { t } = useTranslation()
  /**
   * Format duration from seconds to MM:SS
   */
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  /**
   * Difficulty badge colors
   */
  const difficultyColors = {
    beginner: 'bg-green-100 text-green-800',
    intermediate: 'bg-yellow-100 text-yellow-800',
    advanced: 'bg-red-100 text-red-800'
  }

  /**
   * Format view count
   */
  const formatViews = (count) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`
    }
    return count
  }

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl shadow-md overflow-hidden cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-xl group"
    >
      {/* Thumbnail */}
      <div className="relative aspect-video bg-gray-200 overflow-hidden">
        <img
          src={video.thumbnail}
          alt={video.title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
          loading="lazy"
        />

        {/* Play Overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center">
          <div className="transform scale-0 group-hover:scale-100 transition-transform duration-300">
            <div className="bg-[#29AECB] rounded-full p-4">
              <HiPlay className="text-white text-3xl" />
            </div>
          </div>
        </div>

        {/* Duration Badge */}
        <div className="absolute bottom-2 end-2 bg-black bg-opacity-75 text-white px-2 py-1 rounded text-sm flex items-center gap-1 font-medium">
          <HiClock className="w-4 h-4" />
          {formatDuration(video.duration)}
        </div>

        {/* Category Badge */}
        {video.category_name && (
          <div className="absolute top-2 start-2 bg-[#29AECB] bg-opacity-90 text-white px-3 py-1 rounded-full text-xs font-semibold">
            {video.category_name}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Title */}
        <h3 className="font-semibold text-lg text-gray-900 mb-2 line-clamp-2 group-hover:text-[#29AECB] transition-colors">
          {video.title}
        </h3>

        {/* Description */}
        <p className="text-sm text-gray-600 mb-3 line-clamp-2 leading-relaxed">
          {video.description}
        </p>

        {/* Meta Info */}
        <div className="flex items-center justify-between">
          {/* Difficulty Badge */}
          <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${difficultyColors[video.difficulty]}`}>
            {t(`tutorials.filter.${video.difficulty}`, video.difficulty)}
          </span>

          {/* View Count */}
          <div className="flex items-center gap-1 text-gray-500 text-sm">
            <HiEye className="w-4 h-4" />
            <span>{formatViews(video.views_count)}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VideoCard
